import type { Step } from '@develop/ia-component-library'
import { defineStore } from 'pinia'
import { CheckoutStepKey } from '~/models/checkout'
import { DeliveryOption } from '~/models/checkout-delivery-model-v2'

export const useCheckoutStepsStore = defineStore('checkout-steps', () => {
  const authStore = useAuthStore()
  const deliveryStore = useDeliveryStore()
  const paymentStore = usePaymentStore()
  const checkoutStore = useCheckoutStore()

  const { t } = useTranslation()

  const stepList: Step[] = [
    { label: t('checkout.steps.cart'), key: CheckoutStepKey.Cart, visible: false, active: true },
    { label: t('checkout.steps.customerData'), key: CheckoutStepKey.Customer, visible: true, active: false },
    { label: t('checkout.steps.delivery'), key: CheckoutStepKey.Delivery, visible: true, active: false },
    { label: t('checkout.steps.payment'), key: CheckoutStepKey.Payment, visible: true, active: false },
    { label: t('checkout.steps.overview'), key: CheckoutStepKey.Overview, visible: true, active: false },
    { label: '', key: CheckoutStepKey.ThankYou, visible: false, active: false },
  ]

  const isActive = (step: Step) => step.key === checkoutStore.activeStep

  const steps = computed(() => {
    const [cartStep, customerStep, deliveryStep, paymentStep, overviewStep, finalStep] = stepList

    return [
      { ...cartStep, active: isActive(cartStep) },
      { ...customerStep, visible: !authStore.isAuthenticated, active: isActive(customerStep) },
      { ...deliveryStep, visible: deliveryStore.hasDeliveryOptions, active: isActive(deliveryStep) },
      { ...paymentStep, visible: paymentStore.hasPaymentOptions, active: isActive(paymentStep) },
      overviewStep,
      finalStep,
    ] as Step[]
  })

  const getNextStep = async (currentStep: CheckoutStepKey, direction: 'forward' | 'back'): Promise<CheckoutStepKey> => {
    checkoutStore.isPreviousStepCustomer = currentStep === 'customer'

    if (direction === 'forward') {
      switch (currentStep) {
        case CheckoutStepKey.Cart: {
          if (!deliveryStore.hasDeliveryOptions) {
            deliveryStore.setDeliveryType(DeliveryOption.Pickup)
            await deliveryStore.confirmDeliveryType(DeliveryOption.Pickup)
          }
          if (!paymentStore.hasPaymentOptions) {
            await paymentStore.confirmPayment(null)
            paymentStore.setAddress(null)
            paymentStore.setMethod('in-pharmacy')
          }

          if (!authStore.isAuthenticated)
            return CheckoutStepKey.Customer
          if (deliveryStore.hasDeliveryOptions)
            return CheckoutStepKey.Delivery
          if (paymentStore.hasPaymentOptions)
            return CheckoutStepKey.Payment
          return CheckoutStepKey.Overview
        }
        case CheckoutStepKey.Customer:
          if (deliveryStore.hasDeliveryOptions)
            return CheckoutStepKey.Delivery
          if (paymentStore.hasPaymentOptions)
            return CheckoutStepKey.Payment
          return CheckoutStepKey.Overview
        case CheckoutStepKey.Delivery:
          if (paymentStore.hasPaymentOptions)
            return CheckoutStepKey.Payment

          return CheckoutStepKey.Overview
        case CheckoutStepKey.Payment:
          return CheckoutStepKey.Overview
        case CheckoutStepKey.Overview:
          return CheckoutStepKey.ThankYou
      }
    } else {
      switch (currentStep) {
        case CheckoutStepKey.Cart:
        case CheckoutStepKey.Customer:
          return CheckoutStepKey.Cart
        case CheckoutStepKey.Delivery:
          if (!authStore.isAuthenticated)
            return CheckoutStepKey.Customer
          return CheckoutStepKey.Cart
        case CheckoutStepKey.Payment:
          if (deliveryStore.hasDeliveryOptions)
            return CheckoutStepKey.Delivery
          if (!authStore.isAuthenticated)
            return CheckoutStepKey.Customer
          return CheckoutStepKey.Cart
        case CheckoutStepKey.Overview:
          if (paymentStore.hasPaymentOptions)
            return CheckoutStepKey.Payment
          if (deliveryStore.hasDeliveryOptions)
            return CheckoutStepKey.Delivery
          if (!authStore.isAuthenticated)
            return CheckoutStepKey.Customer
          return CheckoutStepKey.Cart
      }
    }
    return CheckoutStepKey.Cart
  }

  const getStepKeyByLabel = (name: string) => {
    switch (name.toLocaleLowerCase()) {
      case 'kundendaten':
        return CheckoutStepKey.Customer
      case 'lieferart':
        return CheckoutStepKey.Delivery
      case 'zahlungsart':
        return CheckoutStepKey.Payment
      case 'uberprufen':
        return CheckoutStepKey.Overview
      case 'danke':
        return CheckoutStepKey.ThankYou
      case 'warenkorb':
      default:
        return CheckoutStepKey.Cart
    }
  }

  return {
    steps,
    getNextStep,
    getStepKeyByLabel,
  }
})
